import { reportSessionEvent, shouldReloadForNewVersion } from './sessionTrace';

/** Baked into the running JS at Docker / CRA start. Not the file on the SPA origin. */
export const APP_VERSION = process.env.REACT_APP_VERSION || '0.0.0';

/** Local CRA: set REACT_APP_SKIP_VERSION_SYNC=1 so a live API bump does not reload forever. */
const SKIP_VERSION_SYNC =
  process.env.NODE_ENV !== 'test' &&
  (process.env.REACT_APP_SKIP_VERSION_SYNC === '1' ||
    process.env.REACT_APP_SKIP_VERSION_SYNC === 'true');

const CHECK_INTERVAL_MS = 15 * 1000;
const MIN_CHECK_GAP_MS = 3000;
const VERSION_CHANNEL = 'cdd-app-version';
const RELEASE_VERSION = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/;

let reloadScheduled = false;
let checkInFlight = null;
let lastCheckAt = 0;
let versionChannel = null;

export function isReleaseSemver(value) {
  return RELEASE_VERSION.test(String(value ?? '').trim());
}

export function normalizePublishedVersion(raw) {
  let value = String(raw ?? '').trim();
  if (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"))
  ) {
    value = value.slice(1, -1).trim();
  }
  if (/^version/i.test(value)) {
    value = value.replace(/^version/i, '');
  }
  return value;
}

export function isMaintenanceDocument(text) {
  const body = String(text ?? '');
  return body.includes('Application indisponible') || body.includes('cdd:role=frontend-maintenance');
}

export function parseSpaVersionPayload(text) {
  const trimmed = String(text ?? '').trim();
  if (!trimmed) {
    return { error: 'empty' };
  }
  if (isMaintenanceDocument(trimmed)) {
    return { maintenance: true };
  }
  if (!trimmed.startsWith('{')) {
    return { error: 'not_json' };
  }
  try {
    const data = JSON.parse(trimmed);
    const version = normalizePublishedVersion(data?.version);
    if (!version) {
      return { error: 'empty_version' };
    }
    if (!isReleaseSemver(version)) {
      return { error: 'invalid_version' };
    }
    return { version };
  } catch {
    return { error: 'invalid_json' };
  }
}

export function versionsDiverge(runningVersion, publishedVersion) {
  const running = normalizePublishedVersion(runningVersion);
  const published = normalizePublishedVersion(publishedVersion);
  return Boolean(running && published && running !== published);
}

function getVersionChannel() {
  if (versionChannel || typeof BroadcastChannel === 'undefined') {
    return versionChannel;
  }
  try {
    versionChannel = new BroadcastChannel(VERSION_CHANNEL);
  } catch {
    versionChannel = null;
  }
  return versionChannel;
}

export function reloadForNewVersion(serverVersion, source = 'spa', { broadcast = true } = {}) {
  if (SKIP_VERSION_SYNC || reloadScheduled) {
    return false;
  }
  if (!shouldReloadForNewVersion()) {
    return false;
  }
  reloadScheduled = true;

  if (broadcast) {
    try {
      getVersionChannel()?.postMessage({ type: 'reload', version: serverVersion, source });
    } catch {
      // ignore
    }
  }

  const url = new URL(window.location.href);
  url.searchParams.set('_v', serverVersion);
  window.location.replace(url.toString());
  return true;
}

async function fetchSpaPublishedVersion() {
  const response = await fetch(`/version.json?_=${Date.now()}`, {
    cache: 'no-store',
    credentials: 'same-origin',
  });

  if (response.status === 503) {
    return { maintenance: true };
  }

  const text = await response.text();
  if (isMaintenanceDocument(text)) {
    return { maintenance: true };
  }
  if (!response.ok) {
    return { error: `http_${response.status}` };
  }

  return parseSpaVersionPayload(text);
}

async function checkAppVersionNow() {
  try {
    const spa = await fetchSpaPublishedVersion();

    if (spa.maintenance) {
      void reportSessionEvent('ui.version_reload_deferred', {
        reason: 'maintenance',
        path: typeof window !== 'undefined' ? window.location.pathname : '',
      });
      return false;
    }

    if (spa.error) {
      void reportSessionEvent('ui.version_check_failed', {
        level: 'warn',
        reason: spa.error,
        path: typeof window !== 'undefined' ? window.location.pathname : '',
      });
      return false;
    }

    if (versionsDiverge(APP_VERSION, spa.version)) {
      return reloadForNewVersion(spa.version, 'spa');
    }
  } catch (error) {
    const message = error?.message || 'network';
    console.warn('Version check failed:', message);
    void reportSessionEvent('ui.version_check_failed', {
      level: 'warn',
      reason: message,
      path: typeof window !== 'undefined' ? window.location.pathname : '',
    });
  }

  return false;
}

/**
 * Compare the running bundle with the SPA-origin published version (`/version.json`).
 * Reload only when that same-origin file is a different release — never on ALB 503.
 */
export function checkAppVersion() {
  if (SKIP_VERSION_SYNC) {
    return Promise.resolve(false);
  }
  if (checkInFlight) {
    return checkInFlight;
  }
  const now = Date.now();
  if (lastCheckAt && now - lastCheckAt < MIN_CHECK_GAP_MS) {
    return Promise.resolve(false);
  }

  checkInFlight = checkAppVersionNow().finally(() => {
    lastCheckAt = Date.now();
    checkInFlight = null;
  });
  return checkInFlight;
}

/** Called from API responses (`X-App-Version`) so a live tab does not wait for the next interval. */
export function noteServerAppVersion(publishedVersion) {
  const version = normalizePublishedVersion(publishedVersion);
  if (!isReleaseSemver(version) || !versionsDiverge(APP_VERSION, version)) {
    return;
  }
  void checkAppVersion();
}

function isChunkLoadFailure(message, reason) {
  const text = `${message ?? ''} ${reason?.message ?? ''} ${reason?.name ?? ''}`;
  return text.includes('Loading chunk') || text.includes('ChunkLoadError');
}

/**
 * Keep the running tab on the published SPA. Same-origin `/version.json` is the source of truth.
 * Also reload on lazy-chunk failures after a deploy (stale index.html + new hashed chunks).
 */
export function installAppVersionSync() {
  const channel = getVersionChannel();
  const onBroadcast = (event) => {
    if (event?.data?.type === 'reload' && event.data.version) {
      reloadForNewVersion(event.data.version, event.data.source || 'broadcast', { broadcast: false });
    }
  };
  channel?.addEventListener('message', onBroadcast);

  void checkAppVersion();

  const intervalId = setInterval(() => {
    void checkAppVersion();
  }, CHECK_INTERVAL_MS);

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      void checkAppVersion();
    }
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  const onPageShow = (event) => {
    if (event.persisted) {
      void checkAppVersion();
    }
  };
  window.addEventListener('pageshow', onPageShow);

  const onWindowError = (event) => {
    if (isChunkLoadFailure(event?.message, event?.error)) {
      reloadForNewVersion('chunk', 'chunk');
    }
  };
  window.addEventListener('error', onWindowError);

  const onUnhandledRejection = (event) => {
    if (isChunkLoadFailure(null, event?.reason)) {
      reloadForNewVersion('chunk', 'chunk');
    }
  };
  window.addEventListener('unhandledrejection', onUnhandledRejection);

  return () => {
    clearInterval(intervalId);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pageshow', onPageShow);
    window.removeEventListener('error', onWindowError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
    channel?.removeEventListener('message', onBroadcast);
  };
}

export function resetAppVersionSyncForTests() {
  reloadScheduled = false;
  checkInFlight = null;
  lastCheckAt = 0;
}
